import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Context } from '@front10/helpers/dist/Context';
/** @jsx jsx */
import { Button, jsx, Inline } from '@front10/base-elements';
import LocationSelector from '../LocationSelector';
import Column from '../../elements/Col';
import AirportIataCode from '../../containers/_components/AirportIataCode';
import { MobileFooter } from '../../lab/MobileFilterView';
import { ThemeProvider } from '@front10/base-elements';
import { EmotionCache } from '../../containers/_commons/emotionCache';

export default function OriginDestinationFilter({
  origin,
  destination,
  showOrigin,
  showDestination,
  showAirportIataCode,
  showLabels,
  components,
  navigation,
  placeholderOriginKey,
  placeholderDestinationKey,
  showInline,
  isFullMode,
  enabledFooter,
  footerContainer,
  buttonsContainer,
  clearButtonSettings,
  doneButtonSettings,
  allowOverlayMultiple = false
}) {
  const { translate, direction, theme } = useContext(Context);
  const {
    origin: originSettings,
    destination: destinationSettings,
    originColumn,
    destinationColumn
  } = components;

  const clearButtonLabelKey =
    clearButtonSettings?.clearButtonLabelKey ?? 'cmp-location-selector-clear-button';
  const doneButtonLabelKey =
    doneButtonSettings?.doneButtonLabelKey ?? 'cmp-location-selector-done-button';

  const originLocationSelectorRef = React.useRef(null);
  const destinationLocationSelectorRef = React.useRef(null);
  const doneButtonRef = React.useRef(null);
  const [activateDoneButton, setActivateDoneButton] = React.useState(false);

  useEffect(() => {
    if (allowOverlayMultiple) {
      if (origin.valueSelect === undefined && origin.value && origin.value !== '') {
        setActivateDoneButton(true);
      } else {
        setActivateDoneButton(false);
      }
    }
  }, [navigation, origin, destination, allowOverlayMultiple]);

  useEffect(() => {
    if (enabledFooter && navigation === 'all') {
      if (origin.value !== origin.valueSelect) {
        originLocationSelectorRef.current?.update(origin.value);
        origin.selectOrigin({ value: origin.value });
        setActivateDoneButton(false);
      }
      if (destination.value !== destination.valueSelect) {
        destinationLocationSelectorRef.current?.update(destination.value);
        destination.selectDestination({ value: destination.value });
        setActivateDoneButton(false);
      }
    }
  }, [navigation, origin, destination, enabledFooter]);

  function renderItem(item, locationProps) {
    return (
      <AirportIataCode
        item={item}
        showAirportIataCode={showAirportIataCode}
        {...locationProps.listItem}
      />
    );
  }

  const getOriginDestination = (showInline) => (
    <>
      {showOrigin && navigation !== 'Destination' && (
        <Column xs={showInline ? (isFullMode ? '12' : '6') : '12'} {...originColumn}>
          <LocationSelector
            ref={originLocationSelectorRef}
            flex="1"
            marginBottom={showDestination ? '1' : undefined}
            isMobile
            showLabel={showLabels}
            {...originSettings}
            {...origin}
            onChange={enabledFooter ? origin.selectOrigin : origin.onChange}
            onClear={
              enabledFooter
                ? () => {
                    origin.selectOrigin({ value: undefined });
                    setActivateDoneButton(true);
                  }
                : origin.onClear
            }
            defaultValue={enabledFooter ? origin.valueSelect : origin.value}
            options={origin.data}
            loading={origin.isLoading}
            itemRenderer={renderItem}
            dataTestId="locationSelector-origin"
            placeholder={translate(placeholderOriginKey, 'Select Departure City')}
          />
        </Column>
      )}
      {showDestination && navigation !== 'Origin' && (
        <Column xs={showInline ? (isFullMode ? '12' : '6') : '12'} {...destinationColumn}>
          <LocationSelector
            ref={destinationLocationSelectorRef}
            flex="1"
            isMobile
            showLabel={showLabels}
            labelKey="destination"
            {...destinationSettings}
            {...destination}
            onChange={enabledFooter ? destination.selectDestination : destination.onChange}
            onClear={
              enabledFooter
                ? () => {
                    destination.selectDestination({ value: undefined });
                    setActivateDoneButton(true);
                  }
                : destination.onClear
            }
            defaultValue={enabledFooter ? destination.valueSelect : destination.value}
            options={destination.data}
            loading={destination.isLoading}
            itemRenderer={renderItem}
            dataTestId="locationSelector-destination"
            placeholder={translate(placeholderDestinationKey, 'Select Destination City')}
          />
        </Column>
      )}
      {enabledFooter && (navigation === 'Destination' || navigation === 'Origin') && (
        <EmotionCache direction={direction}>
          <ThemeProvider theme={theme?.designTokens ?? undefined}>
            <MobileFooter align="end" sx={{ width: '100%', right: 0, p: '10px' }}>
              <Inline gap={2} align="end">
                <Button
                  data-testId="buttonFooterClear"
                  type="button"
                  disabled={
                    navigation === 'Origin'
                      ? !Boolean(origin.valueSelect)
                      : navigation === 'Destination'
                      ? !Boolean(destination.valueSelect)
                      : true
                  }
                  variant="text"
                  onClick={() => {
                    if (navigation === 'Origin') {
                      origin.selectOrigin({ value: undefined });
                      destinationLocationSelectorRef?.current?.onClear();
                    }
                    if (navigation === 'Destination') {
                      destination.selectDestination({ value: undefined });
                      destinationLocationSelectorRef?.current?.onClear();
                    }
                    setActivateDoneButton(true);
                    doneButtonRef?.current?.focus();
                  }}
                  className="footerButtons"
                  {...clearButtonSettings}
                >
                  {translate(clearButtonLabelKey, 'Clear')}
                </Button>
                <Button
                  data-testId={'buttonFooterDone'}
                  ref={doneButtonRef}
                  disabled={
                    navigation === 'Origin'
                      ? !Boolean(origin.valueSelect) && !activateDoneButton
                      : navigation === 'Destination'
                      ? !Boolean(destination.valueSelect) && !activateDoneButton
                      : true
                  }
                  onClick={() => {
                    if (navigation === 'Origin') {
                      if (origin.valueSelect) {
                        origin.onChange({ value: origin.valueSelect });
                      } else {
                        origin.onClear({ notifyFullMode: false });
                      }
                    }
                    if (navigation === 'Destination') {
                      if (destination.valueSelect) {
                        destination.onChange({ value: destination.valueSelect });
                      } else {
                        destination.onClear({ notifyFullMode: false });
                      }
                    }
                    setActivateDoneButton(false);
                  }}
                  type="button"
                  variant="text"
                  {...doneButtonSettings}
                >
                  {translate(doneButtonLabelKey, 'Done')}
                </Button>
              </Inline>
            </MobileFooter>
          </ThemeProvider>
        </EmotionCache>
      )}
    </>
  );

  const OriginDestinationCmp = getOriginDestination(showInline);
  return (
    <>
      {showInline ? (
        <div className="fr-col">
          <div className="fr-row fr-no-gutters">{OriginDestinationCmp}</div>
        </div>
      ) : (
        OriginDestinationCmp
      )}
    </>
  );
}

OriginDestinationFilter.propTypes = {
  showOrigin: PropTypes.bool,
  showDestination: PropTypes.bool,
  showAirportIataCode: PropTypes.bool,
  showLabels: PropTypes.bool,
  components: PropTypes.shape({
    origin: PropTypes.objectOf(PropTypes.any),
    destination: PropTypes.objectOf(PropTypes.any),
    originColumn: PropTypes.objectOf(PropTypes.any),
    destinationColumn: PropTypes.objectOf(PropTypes.any)
  }),
  origin: PropTypes.shape({
    value: PropTypes.string,
    isLoading: PropTypes.bool,
    isError: PropTypes.bool,
    data: PropTypes.arrayOf(PropTypes.any)
  }),
  destination: PropTypes.shape({
    value: PropTypes.string,
    isLoading: PropTypes.bool,
    isError: PropTypes.bool,
    data: PropTypes.arrayOf(PropTypes.any)
  }),
  navigation: PropTypes.string,
  placeholderOriginKey: PropTypes.string,
  placeholderDestinationKey: PropTypes.string,
  enabledFooter: PropTypes.bool
};

OriginDestinationFilter.defaultProps = {
  showOrigin: true,
  showDestination: false,
  showAirportIataCode: false,
  showLabels: false,
  components: {
    origin: {},
    destination: {},
    originColumn: {},
    destinationColumn: {}
  },
  origin: {
    value: PropTypes.string,
    isLoading: PropTypes.bool,
    isError: PropTypes.bool,
    data: PropTypes.arrayOf(PropTypes.any)
  },
  destination: {
    value: PropTypes.string,
    isLoading: PropTypes.bool,
    isError: PropTypes.bool,
    data: PropTypes.arrayOf(PropTypes.any)
  },
  navigation: 'all',
  placeholderOriginKey: 'cmp-location-origin-placeholder',
  placeholderDestinationKey: 'cmp-location-destination-placeholder',
  enabledFooter: true
};
